import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';

declare const google: any;

/**
 * Local Ivy-compatible re-implementation of the directive shipped with the
 * unmaintained `ngx-google-places-autocomplete` package. Mirrors the original
 * selector/exportAs/inputs/outputs so existing templates continue to work.
 */
@Directive({
    standalone: false,
    selector: '[ngx-google-places-autocomplete]',
    exportAs: 'ngx-places'
})
export class GooglePlaceDirective implements AfterViewInit, OnDestroy {
    @Input() options: any;
    @Output() onAddressChange = new EventEmitter<any>();

    place: any;
    private autocomplete: any;
    private eventListener: any;
    private readonly keydownHandler = (event: KeyboardEvent) => {
        if (!event.key) {
            return;
        }
        if (event.key.toLowerCase() === 'enter' && event.target === this.el.nativeElement) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    constructor(private el: ElementRef, private ngZone: NgZone) {}

    ngAfterViewInit(): void {
        if (!this.isGoogleLibAvailable()) {
            return;
        }
        if (!this.options) {
            this.options = {};
        }
        this.initialize();
    }

    ngOnDestroy(): void {
        if (this.autocomplete && google?.maps?.event && this.eventListener) {
            google.maps.event.removeListener(this.eventListener);
        }
        this.el.nativeElement.removeEventListener('keydown', this.keydownHandler);
    }

    reset(): void {
        if (!this.autocomplete) {
            return;
        }
        if (this.options?.componentRestrictions) {
            this.autocomplete.setComponentRestrictions(this.options.componentRestrictions);
        }
        if (this.options?.types) {
            this.autocomplete.setTypes(this.options.types);
        }
    }

    private isGoogleLibAvailable(): boolean {
        return typeof google !== 'undefined' && !!google.maps && !!google.maps.places;
    }

    private initialize(): void {
        this.autocomplete = new google.maps.places.Autocomplete(this.el.nativeElement, this.options);
        if (!this.autocomplete) {
            return;
        }

        this.eventListener = this.autocomplete.addListener('place_changed', () => {
            this.handleChangeEvent();
        });

        this.el.nativeElement.addEventListener('keydown', this.keydownHandler);
    }

    private handleChangeEvent(): void {
        this.ngZone.run(() => {
            this.place = this.autocomplete.getPlace();
            if (this.place) {
                this.onAddressChange.emit(this.place);
            }
        });
    }
}
